import { useEffect, useRef, useState } from 'react';
import { Menu } from 'lucide-react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';

import { AdminNavigationProvider } from '../navigation/AdminNavigationContext';
import { useAdminNavigation } from '../navigation/useAdminNavigation';
import { getSectionByPath } from '../navigation/adminRoutes';
import { rememberAdminLocation } from '../navigation/adminPlaceMemory';
import { canAccessSection as checkSectionAccess, getAdminUser } from '../navigation/sectionAccess';
import { CommandPaletteProvider, CommandPalette, useCommandPalette } from '../command-palette';
import { WhatsNewProvider } from '../release-notes';


import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import AdminMobileDrawer from './AdminMobileDrawer';
import AdminMobileBottomNav from './AdminMobileBottomNav';
import { getAdminShellGeometry } from './adminShellGeometry';

import usePublishStatus from '@/hooks/usePublishStatus';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Inner layout that uses the navigation context
const AdminLayoutInner = ({ onLogout }) => {
  const location = useLocation();
  const { hasPendingChanges } = usePublishStatus();
  const overlayTriggerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const restoreOverlayFocusRef = useRef(false);
  const { closePalette } = useCommandPalette();
  const {
    desktopOverlayOpen,
    setDesktopOverlayOpen,
    railExpanded,
    setRailExpanded,
    navigationPreferences,
  } = useAdminNavigation();
  const sidebarMode = navigationPreferences.sidebar_mode || 'rail';
  const shellGeometry = getAdminShellGeometry(sidebarMode, railExpanded);

  useEffect(() => {
    setDesktopOverlayOpen(false);
    setRailExpanded(false);
  }, [location.pathname, setDesktopOverlayOpen, setRailExpanded]);

  useEffect(() => {
    const desktopMedia = window.matchMedia?.('(min-width: 769px)');
    if (!desktopMedia) return undefined;
    const releaseOverlayOnMobile = (event) => {
      if (event.matches) return;
      restoreOverlayFocusRef.current = false;
      setDesktopOverlayOpen(false);
    };
    desktopMedia.addEventListener('change', releaseOverlayOnMobile);
    return () => desktopMedia.removeEventListener('change', releaseOverlayOnMobile);
  }, [setDesktopOverlayOpen]);

  useEffect(() => {
    if (desktopOverlayOpen || !restoreOverlayFocusRef.current) return undefined;
    restoreOverlayFocusRef.current = false;
    const focusFrame = window.requestAnimationFrame(() => overlayTriggerRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [desktopOverlayOpen]);

  useEffect(() => {
    if (!desktopOverlayOpen) return undefined;
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(
      overlayContainerRef.current?.querySelector('[role="dialog"]')
        ?.querySelectorAll(focusableSelector) || [],
    ).filter((element) => element.offsetParent !== null);
    const focusFrame = window.requestAnimationFrame(() => focusables()[0]?.focus());
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        restoreOverlayFocusRef.current = true;
        setDesktopOverlayOpen(false);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopPropagation();
        closePalette();
      }
      if (event.key === 'Tab') {
        const items = focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePalette, desktopOverlayOpen, setDesktopOverlayOpen]);

  // Get current user's permissions from localStorage (role + is_protected + allowed_sections)
  const adminUser = getAdminUser();

  // Helper: check if user can access a section (role-aware — see sectionAccess.js)
  const canAccessSection = (sectionId) => checkSectionAccess(sectionId, adminUser);

  // Navigation links resolve remembered destinations during render, before
  // this effect records the current place; re-render them when it changes.
  const [, setPlaceMemoryRevision] = useState(0);
  useEffect(() => {
    const changed = rememberAdminLocation(location, adminUser, (sectionId) => (
      checkSectionAccess(sectionId, adminUser)
    ));
    if (changed) setPlaceMemoryRevision((revision) => revision + 1);
  }, [adminUser, location]);

  // Check if current route's section is accessible
  const currentSection = getSectionByPath(location.pathname);
  if (currentSection && !canAccessSection(currentSection.id)) {
    // Redirect to dashboard if user doesn't have access to this section
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen admin-bg">
      {/* Mobile drawer */}
      <div className="admin-desktop:hidden">
        <AdminMobileDrawer onLogout={onLogout} canAccessSection={canAccessSection} />
      </div>

      {/* Desktop sidebar */}
      <div className={cn(
        'hidden admin-desktop:block',
        sidebarMode === 'overlay' && 'admin-desktop:hidden',
      )}>
        <AdminSidebar onLogout={onLogout} canAccessSection={canAccessSection} />
      </div>

      {sidebarMode === 'overlay' && (
        <Button
          ref={overlayTriggerRef}
          type="button"
          variant="outline"
          size="icon"
          className="fixed left-3 top-3 z-50 hidden h-[44px] w-[44px] shadow-md admin-desktop:inline-flex"
          onClick={() => {
            closePalette();
            setDesktopOverlayOpen(true);
          }}
          aria-label="Open navigation"
          aria-expanded={desktopOverlayOpen}
          aria-controls="admin-desktop-overlay"
          aria-hidden={desktopOverlayOpen ? 'true' : undefined}
          inert={desktopOverlayOpen ? true : undefined}
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {sidebarMode === 'overlay' && desktopOverlayOpen && (
        <div
          ref={overlayContainerRef}
          id="admin-desktop-overlay"
          className="fixed inset-0 z-[60] hidden admin-desktop:block"
        >
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-0 h-full w-full bg-black/45"
            aria-label="Close navigation"
            onClick={() => {
              restoreOverlayFocusRef.current = true;
              setDesktopOverlayOpen(false);
            }}
          />
          <AdminSidebar
            overlay
            onLogout={onLogout}
            canAccessSection={canAccessSection}
            onDismiss={() => {
              restoreOverlayFocusRef.current = true;
              setDesktopOverlayOpen(false);
            }}
          />
        </div>
      )}

      <div
        data-testid="admin-shell-background"
        aria-hidden={desktopOverlayOpen ? 'true' : undefined}
        inert={desktopOverlayOpen ? true : undefined}
      >
        {/* Main content area */}
        <main
        className={cn(
          'min-h-screen transition-[margin] duration-200 ease-out motion-reduce:transition-none',
          shellGeometry.mainOffsetClass,
          // Add bottom padding for mobile bottom nav
          'pb-[calc(5rem+env(safe-area-inset-bottom,0px))] admin-desktop:pb-0'
        )}
      >
        <AdminHeader onLogout={onLogout} />

        {/* Page content - pt accounts for fixed header, extra padding when publish banner visible */}
        {/* Mobile header: h-14 (56px), Desktop: h-16 (64px), Publish banner adds ~52px */}
        {/* --admin-content-top/bottom mirror the padding above and below (plus the
            mobile bottom nav) so a cockpit can size a finite workspace (OWZ-71). */}
        <div className={cn(
          'max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 pb-4 admin-desktop:pb-6',
          '[--admin-content-bottom:calc(6rem+env(safe-area-inset-bottom,0px))] admin-desktop:[--admin-content-bottom:1.5rem]',
          hasPendingChanges
            ? 'pt-32 admin-desktop:pt-36 [--admin-content-top:8rem] admin-desktop:[--admin-content-top:9rem]'
            : 'pt-20 admin-desktop:pt-24 [--admin-content-top:5rem] admin-desktop:[--admin-content-top:6rem]'
        )}>
          <Outlet />
        </div>
        </main>

        {/* Mobile bottom nav */}
        <div className="admin-desktop:hidden">
          <AdminMobileBottomNav canAccessSection={canAccessSection} />
        </div>

        {/* Command Palette - kept outside main content for proper z-index */}
        <CommandPalette canAccessSection={canAccessSection} />
      </div>
    </div>
  );
};

// Wrapper component that provides the navigation and command palette contexts
const AdminLayout = ({ onLogout }) => {
  return (
    <AdminNavigationProvider>
      <WhatsNewProvider>
        <CommandPaletteProvider>
          <AdminLayoutInner onLogout={onLogout} />
        </CommandPaletteProvider>
      </WhatsNewProvider>
    </AdminNavigationProvider>
  );
};

export default AdminLayout;
